import { useEffect } from 'react';
import { getThirdPartyLoader } from '../utils/optimizedThirdPartyLoader';

/**
 * Enhanced AdSense Script Component with Strategic Ad Placement Hints
 * Uses optimized third-party loader to ensure scripts load only after render completion
 */

// Declare adsbygoogle for TypeScript
declare global {
  interface Window {
    adsbygoogle: any[];
    googletag: any;
    __google_ad_placement_hints: any;
  }
}

// Strategic Ad Placement Configuration
const adPlacementHints = {
  // Mobile TikTok-style Feed Placements
  mobile_tiktok: {
    'mobile-top-banner': {
      position: 'fixed-top',
      frequency: 'every-post',
      size: { width: '100%', height: '60px' },
      priority: 'high',
      implemented: true
    },
    'mobile-between-posts': {
      position: 'in-feed',
      frequency: 'every-4-posts',
      size: { width: '100%', height: '100vh' },
      priority: 'high',
      format: 'native-fullscreen',
      implemented: true
    },
    'mobile-bottom-action': {
      position: 'floating-bottom',
      frequency: 'persistent',
      size: { width: '100%', height: '50px' },
      priority: 'medium',
      implemented: false
    }
  },
  
  // Mobile Single Post Feed Placements  
  mobile_single: {
    'mobile-article-header': {
      position: 'below-title',
      frequency: 'every-post',
      size: { width: '100%', height: '200px' },
      priority: 'high',
      implemented: false
    },
    'mobile-mid-article': {
      position: 'mid-content',
      frequency: 'after-paragraph-3',
      size: { width: '100%', height: '250px' },
      priority: 'very-high',
      format: 'native-article',
      implemented: false
    },
    'mobile-related-posts': {
      position: 'between-related',
      frequency: 'every-6-items',
      size: { width: '100%', height: '200px' },
      priority: 'medium',
      implemented: false
    }
  },

  // Desktop News Feed Placements
  desktop_news: {
    'desktop-header-leaderboard': {
      position: 'below-navigation',
      frequency: 'persistent',
      size: { width: '728px', height: '90px' },
      priority: 'high',
      implemented: false
    },
    'desktop-sidebar-rail': {
      position: 'right-sidebar',
      frequency: 'persistent-sticky',
      size: { width: '300px', height: '600px' },
      priority: 'very-high',
      format: 'skyscraper',
      implemented: false
    },
    'desktop-in-grid': {
      position: 'grid-item',
      frequency: 'every-9-cards',
      size: { width: '100%', height: '250px' },
      priority: 'high',
      format: 'native-card',
      implemented: false
    }
  },

  // Desktop Single Post Placements
  desktop_single: {
    'desktop-article-sidebar': {
      position: 'right-column',
      frequency: 'persistent',
      size: { width: '300px', height: '600px' },
      priority: 'very-high',
      format: 'skyscraper',
      implemented: false
    },
    'desktop-above-fold': {
      position: 'before-content',
      frequency: 'every-post',
      size: { width: '728px', height: '90px' },
      priority: 'high',
      implemented: false
    },
    'desktop-in-article': {
      position: 'mid-content',
      frequency: 'after-paragraph-4',
      size: { width: '336px', height: '280px' },
      priority: 'very-high',
      format: 'rectangle',
      implemented: false
    }
  }
};

const AdSenseScript: React.FC = () => {
  useEffect(() => {
    // Get the optimized third-party loader instance
    const loader = getThirdPartyLoader();
    
    // 🎯 NEW: Register ad placement hints for Google AdSense Auto Ads
    const setupAdPlacementHints = () => {
      try {
        // Store placement configuration globally for AdSense to discover
        window.__google_ad_placement_hints = adPlacementHints;
        
        // Send placement hints to Google AdSense via gtag if available
        if (typeof window.gtag === 'function') {
          window.gtag('config', 'AW-CONVERSION_ID', {
            custom_map: {
              ad_placement_hints: JSON.stringify(adPlacementHints)
            }
          });
        }

        // Configure automatic ad placement suggestions
        if (window.adsbygoogle) {
          window.adsbygoogle.push({
            google_ad_client: "ca-pub-8980858398695883", // ✅ REAL PUBLISHER ID
            enable_page_level_ads: true,
            overlays: {
              bottom: true // Enable mobile bottom overlay ads
            },
            anchor_ad_optimization: {
              enabled: true,
              frequency_cap: 1 // Limit frequency for better UX
            }
          });
        }

        console.log('🎯 [AdSenseScript] Strategic ad placement hints configured:', {
          totalPlacements: Object.values(adPlacementHints).reduce((acc, category) => acc + Object.keys(category).length, 0),
          implementedPlacements: Object.values(adPlacementHints).reduce((acc, category) => 
            acc + Object.values(category).filter((placement: any) => placement.implemented).length, 0
          ),
          categories: Object.keys(adPlacementHints)
        });

      } catch (error) {
        console.error('❌ [AdSenseScript] Error setting up ad placement hints:', error);
      }
    };
    
    // Signal to React that the app is ready for third-party loading
    const signalReactReady = () => {
      // Setup ad placement hints first
      setupAdPlacementHints();
      
      // Then dispatch custom event for render completion detection
      window.dispatchEvent(new CustomEvent('react-app-ready'));
      console.log('📡 [AdSenseScript] React app ready signal dispatched with ad placement hints');
    };

    // Delay signal slightly to ensure component is fully mounted
    const readyTimeout = setTimeout(signalReactReady, 100);

    // Log current loader status for debugging
    if (process.env.NODE_ENV === 'development') {
      const status = loader.getStatus();
      console.log('🔧 [AdSenseScript] Loader Status:', status);
      console.log('🎯 [AdSenseScript] Ad Placement Configuration:', adPlacementHints);
      
      // Force load for development testing
      if (window.location.hostname === 'localhost') {
        console.log('🔧 [AdSenseScript] Development mode - checking loader state');
      }
    }

    return () => {
      clearTimeout(readyTimeout);
    };
  }, []);

  // This component doesn't render anything
  return null;
};

export default AdSenseScript; 